/**
 * Presence: who else has this proposal / publication open, shown as avatars at the top right
 * of the section editor. Each open writer sends a heartbeat every 20 s (paused while the tab is
 * hidden); the server forgets anyone not heard from for 45 s.
 */
import React, { useEffect, useState } from 'react';
import { Avatar, AvatarGroup, Badge, Box, Tooltip, Typography } from '@mui/material';
import { axiosService } from 'shared/helpers/axios';
import { authorColor } from 'components/social/profile/reflections/trackChanges';

const HEARTBEAT_MS = 20000;

/** Others present on `proposalId`; reports the section the current user has open. */
export function usePresence(proposalId, sectionId) {
  const [others, setOthers] = useState([]);

  useEffect(() => {
    if (!proposalId) return undefined;
    const url = `/proposal/proposals/${proposalId}/presence/`;
    let cancelled = false;
    const beat = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await axiosService.post(url, { section: sectionId || null });
        if (!cancelled) setOthers(Array.isArray(res.data) ? res.data : []);
      } catch { /* presence is best-effort */ }
    };
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', beat);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [proposalId, sectionId]);

  // Leave when the writer closes (not on every section change)
  useEffect(() => {
    if (!proposalId) return undefined;
    return () => { axiosService.delete(`/proposal/proposals/${proposalId}/presence/`).catch(() => {}); };
  }, [proposalId]);

  return others;
}

const initials = (name = '') => name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';

/** Avatars of the others present; those in `sectionId` get a green dot ("writing here"). */
export default function PresenceAvatars({ others = [], sectionId, max = 5 }) {
  if (!others.length) return null;
  const here = others.filter((o) => o.section_id && String(o.section_id) === String(sectionId));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-label="People with this document open">
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
        {here.length ? `${here.length} writing here` : `${others.length} online`}
      </Typography>
      <AvatarGroup max={max} sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 12, borderWidth: 2 } }}>
        {others.map((o) => {
          const inSection = here.includes(o);
          const where = inSection
            ? 'writing in this section'
            : o.section_title ? `in “${o.section_title}”` : 'viewing the document';
          return (
            <Tooltip key={o.user_id} title={`${o.name} — ${where}`}>
              <Badge
                overlap="circular" variant="dot" invisible={!inSection}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ '& .MuiBadge-dot': { bgcolor: 'success.main', boxShadow: '0 0 0 2px #fff' } }}
              >
                <Avatar src={o.avatar_url || undefined} alt={o.name}
                        sx={{ bgcolor: authorColor(o.user_id), borderColor: inSection ? 'success.main' : undefined }}>
                  {initials(o.name)}
                </Avatar>
              </Badge>
            </Tooltip>
          );
        })}
      </AvatarGroup>
    </Box>
  );
}
